<template>
	<div
		class="cdp p-3 sm:p-6 transition-[padding]"
		:style="{
    '--cdp-hover-bg-color': hoverColor,
    '--cdp-hover-text-color': hoverTextColor,
    '--cdp-selected-bg-color': selectedColor,
    '--cdp-selected-text-color': selectedTextColor,
  }"
	>
		<div class="cdp__container text-center">
			<div class="cdp__header">
				<div class="cdp__header--controls pb-4 flex justify-between items-center">
					<slot name="control:subtract">
						<button
							data-cy="calendar_prev"
							type="button"
							:aria-label="t('navigate_to_name_in_the_calendar', { name: localeFormatDate(previousCalendarMonth) })"
							:class="['control justify-center flex items-center', { 'invisible': !canDecrement} ]"
							:disabled="!canDecrement"
							@click="handleCalendarControl('subtract')"
						>
							<ChevronLeftIcon class="size-3" />
						</button>
					</slot>

					<div class="month">{{ viewer_month }}</div>

					<slot name="control:add">
						<button
							data-cy="calendar_next"
							type="button"
							:aria-label="t('navigate_to_name_in_the_calendar', { name: localeFormatDate(nextCalendarMonth) })"
							:class="['control justify-center flex items-center', { 'invisible': !canIncrement }]"
							:disabled="!canIncrement"
							@click="handleCalendarControl('add')"
						>
							<ChevronRightIcon class="size-3" />
						</button>
					</slot>
				</div>

				<div class="cdp__header--days grid grid-cols-7 gap-x-1.5 mb-2">
					<div v-for="(index, day) in viewer_days" :key="day">{{ day }}</div>
				</div>
			</div>

			<div class="cdp__calendar grid grid-cols-7 gap-x-1.5 gap-y-2" @mouseleave="calendar.hovered_days = []">
				<div
					v-for="(day, index) in viewer_dates"
					:key="day.formatted"
					class="cdp__calendar--day group"
					:class="day.options.classes"
				>
					<button
						type="button"
						:data-cy="`datepicker-day-${day.formatted}`"
						:data-cy-selected="selected_days.includes(day.formatted)"
						:data-cy-selectable="!day.options.inactive || day.options.deblockaged"
						:class="tw([
              // Basis
              'flex rounded-lg bg-surface text-ink items-center justify-center flex-col gap-px w-full h-full cursor-pointer relative transition',

              // Multiple selection; hovered extra nights preview the definitive extra night styling
              calendar.hovered_days.includes(day.formatted) && !(day.options.isHoverExtraNight && day.options.extraNightPrice) && 'bg-(--cdp-hover-bg-color) text-(--cdp-hover-text-color)',
              day.options.isHoverExtraNight && day.options.extraNightPrice && 'bg-primary/10 text-ink ring-1 ring-inset ring-primary/50',

              // Selected state
              selected_days.includes(day.formatted) && 'bg-(--cdp-selected-bg-color) text-(--cdp-selected-text-color)',

              // Active state
              !day.options.inactive && !selected_days.includes(day.formatted) && 'hover:bg-(--cdp-hover-bg-color) hover:text-(--cdp-hover-text-color)',

              // Swimwear day: blue day number and price. Once the day is part of the (pending) selection it
              // follows the resort selection colours, and only the notification dot still marks it.
              !dayInRange(day) && 'group-[.swimwear-day]:text-swimwear',
              !day.options.inactive && !dayInRange(day) && 'hover:group-[.swimwear-day]:bg-swimwear/20',

              // Extra night: only styled when the calendar data carries an extra night price
              day.options.isExtraNight && day.options.extraNightPrice && 'bg-primary/20 text-ink ring-1 ring-inset ring-primary',

              // Inactive state: no card, just a muted day number
              day.options.inactive && !day.options.deblockaged && !selected_days.includes(day.formatted) && 'bg-transparent text-ink-disabled cursor-default',
            ])"
						:tabindex="day.options.inactive && !day.options.deblockaged ? -1 : 0"
						@click="handleDateSelection($event, day, index)"
						@mouseenter="calendar.in_selection ? handleDateHover($event, day) : null"
					>
						<slot name="day" v-bind="day">
							<div class="font-semibold text-base leading-5">
								{{ day.raw.getDate() }}
							</div>
						</slot>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import {computed, reactive} from 'vue';
import { de, enUS, nl } from 'date-fns/locale';
import { twMerge as tw } from 'tailwind-merge';
import { useI18n } from 'vue-i18n';
import ChevronRightIcon from '@thermenresorts/icons/svg/chevron-right.svg';
import ChevronLeftIcon from '@thermenresorts/icons/svg/chevron-left.svg';

import {
  addDays,
  addMonths,
  addYears,
  eachDayOfInterval,
  format, isAfter, isBefore, isSameDay,
  startOfDay,
  lastDayOfWeek,
  setDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subMonths,
} from 'date-fns';

export interface ViewerDate {
  raw: Date
  formatted: string
  options: {
    inactive: boolean
    deblockaged?: boolean
    classes?: string
    price?: string
    date?: string
    status?: string
    isSpecialDay?: boolean
    isSelected?: boolean
    isHovered?: boolean
    firstDayInRange?: boolean
    lastDayInRange?: boolean
    /* a selected night between arrival and departure, billed as an extra night */
    isExtraNight?: boolean
    nightsInRange?: number
    /* the arrival is picked but the departure is still open */
    isPendingArrival?: boolean
    /* a hovered day between the arrival and the hovered departure candidate */
    isHoverExtraNight?: boolean
    extraNightPrice?: number | null
    notifications?: string[]
  }
}

export interface DateOptions {
  [index: string]: {
    inactive: boolean
    classes?: string
    price?: string
    date?: string
    status?: string
    isSpecialDay?: boolean
    extraNightPrice?: number | null
    notifications?: string[]
  }
}

export interface CalendarOptions {
  focus: Date,
  dates: {
    start: Date,
    end: Date | null,
    dateRange: string[]
  },
  in_selection: boolean,
  is_reselected: boolean,
  hovered_days: string[]
}

export interface Props {
  /**
   * v-model of the component.
   */
  startDate?: Date,

  /**
   * End of the date, if `range` is not true, it'll be ignored.
   */
  endDate?: Date | null,

  /**
   * The selected dates in an array, this is never read, only written to.
   */
  dateRange?: string[],

  /**
   * An array of dates to override the calendar with. Through this way, dates can be set inactive or set as a special day.
   */
  dateOptions?: DateOptions,

  /**
   * The visible days in an array, this is never read, only written to.
   */
  dates?: ViewerDate[],

  /**
   * Allows user to set language of the datepicker.
   * Current supported languages: nl, de, en.
   */
  language?: 'nl' | 'en' | 'de' | 'fr'

  /**
   * Allows the user to toggle the calendar into single day mode or range mode
   */
  range?: boolean

  /**
   * Forces the user to always reselect the start date whenever we are in range mode.
   */
  forceReselect?: boolean

  /**
   * Allows user to define static amount of days selected during Range Selection.
   * Whenever number is defined as zero (0), this feature will be disabled.
   */
  staticRange?: number

  /**
   * Allows user to disable the legend
   */
  showLegend?: boolean,

  /**
   * Allows user to define legend items to show underneath the calendar
   */
  legendItems?: string[]

  /**
   * Disables dates that are in the past
   */
  disablePastDays?: boolean

  /**
   * The color for the hover state.
   */
  hoverColor?: string

  /**
   * The color for the hover state.
   */
  hoverTextColor?: string

  /**
   * The background color for the hover state.
   */
  selectedColor?: string

  /**
   * The text color for the hover state.
   */
  selectedTextColor?: string
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  startDate: () => new Date(),
  endDate: () => new Date(),
  dateRange: () => [],
  dateOptions: () => ({}),
  dates: () => [],
  range: false,
  forceReselect: true,
  language: 'en',
  staticRange: 0,
  showLegend: true,
  legendItems: () => [],
  disablePastDays: true,
  hoverColor: 'transparent',
  hoverTextColor: '#000000',
  selectedColor: 'transparent',
  selectedTextColor: '#000000'
});

const emit = defineEmits([
  // prop emits
  'update:startDate',
  'update:endDate',
  'update:dateRange',
  'update:dates',

  // custom emits
  'day:change',
  'month:change',
]);

// Supported languages
const languages = {
  nl: nl,
  de: de,
  en: enUS
};

// Current date
const today: Date = startOfToday();

// Keep track of the dates and statusses of the calendar.
const calendar = reactive<CalendarOptions>({
  // The main focus variable to calculate the month we are in.
  // Selecting the beginning of the month, to prevent collisions on days after the 28th.
  focus: new Date(props.startDate.getFullYear(), props.startDate.getMonth(), 1),

  // The variables to track the calendar selected dates.
  // These are required in order to work with optional props. (end date is not always required..)
  dates: {
    start: props.startDate,
    end: props.endDate,
    dateRange: props.dateRange
  },

  // The variable where we check if we are in a current date selection.
  // This is only possible to be true, whenever `props.range` is set to `true`.
  in_selection: false,

  // The variable where we check if the user is forced into startdate reselection.
  // This is only possible to be true, whenever `props.range=true` and `force_reselect=true`
  is_reselected: false,

  // Hovered days, this is handled by the `handleDateHover()` function. It includes possibly selected days in the calendar whenever in a date range selection.
  // This is only possible to be filled, whenever `props.range` is set to `true`.
  hovered_days: []
});

const calendarStartDate = computed({
  get: () => calendar.dates.start,
  set: (val) => {
    emit('update:startDate', val)
    return calendar.dates.start = val;
  }
});

const calendarEndDate = computed({
  get: () => calendar.dates.end,
  set: (val) => {
    emit('update:endDate', val)
    return calendar.dates.end = val;
  }
});

computed({
  get: () => selected_days,
  set: (val) => emit('update:dateRange', val)
});

computed({
  get: () => viewer_dates,
  set: (val) => emit('update:dates', val)
});

const viewer_dates = computed<ViewerDate[]>(() => {
  const month = calendar.focus.getMonth(),
    year = calendar.focus.getFullYear(),
    startDay = startOfWeek(new Date(year, month, 1), {weekStartsOn: 1}),
    endDay = lastDayOfWeek(new Date(year, month + 1, 0), {weekStartsOn: 1});

  const dates: ViewerDate[] = formatDates(eachDayOfInterval({start: startDay, end: endDay}));
  let had_selection_date = false;

  const selection = selected_days.value;
  const selectionStart = startOfDay(calendarStartDate.value);

  const isPendingSelection = props.range && calendar.in_selection && !calendarEndDate.value;

  return dates.map((date) => {
    const hasDateOptions = date.formatted in props.dateOptions;
    let shouldDeblock = false;

    // #3322: Deblock the next inactive date from the starting date
    if(hasDateOptions && !had_selection_date && props.range && calendar.in_selection && isAfter(date.raw, selectionStart) && props.dateOptions[date.formatted].inactive) {
      had_selection_date = true;
      shouldDeblock = true;
    }

    const isFirstDayInRange = selection.length > 1 && selection[0] === date.formatted;
    const isLastDayInRange = selection.length > 1 && selection[selection.length - 1] === date.formatted;

    date.options = {
      isSelected: selection.includes(date.formatted),
      isHovered: calendar.hovered_days.includes(date.formatted),
      isHoverExtraNight: calendar.hovered_days.length > 1
        && calendar.hovered_days.includes(date.formatted)
        && calendar.hovered_days[0] !== date.formatted
        && calendar.hovered_days[calendar.hovered_days.length - 1] !== date.formatted,
      firstDayInRange: isFirstDayInRange,
      lastDayInRange: isLastDayInRange,
      isExtraNight: selection.length > 1 && selection.includes(date.formatted) && !isFirstDayInRange && !isLastDayInRange,
      nightsInRange: Math.max(selection.length - 1, 0),
      isPendingArrival: isPendingSelection && isSameDay(date.raw, selectionStart),
      ...date.options,
      ...(hasDateOptions ? props.dateOptions[date.formatted] : {}),
      // #3322: Deblock the next inactive date from the starting date
      ...(shouldDeblock ? { deblockaged: true } : {}),
      // #3322: Set inactive en block if we have matched an inactive date when in hotel range selection
      ...(hasDateOptions && had_selection_date && !shouldDeblock && !props.dateOptions[date.formatted].inactive ? {inactive: true, deblockaged: false} : {}),
    }

    return date;
  });
})

const viewer_days = computed<{ [index: string]: number }>(() => {
  const headers: { [index: string]: number } = {};

  Array.from([1, 2, 3, 4, 5, 6, 0]).forEach(number => {
    // Format `EEEEEE` is representing ma/di/wo etc.
    headers[
      localeFormatDate(setDay(new Date(), number), 'EEEEEE')
    ] = number;
  });

  return headers;
})

const viewer_month = computed<string>(() => {
  return localeFormatDate(calendar.focus);
})

const selected_days = computed<string[]>(() => {
  // Whenever we are in range modus
  if (props.range) {
    // Be sure to have an end date, and be sure they're not the same day to prevent invalid range errors of Date-FNS.
    if(calendarEndDate.value && !isSameDay(calendarStartDate.value, calendarEndDate.value) && calendarStartDate.value < calendarEndDate.value) {
      const list = formatDatesAsString(eachDayOfInterval({start: calendarStartDate.value, end: calendarEndDate.value}))

      // Update the start date
      emit('update:dateRange', list)

      return list;
    }
  }

  const list = formatDatesAsString([calendarStartDate.value]);

  // Update the start date
  emit('update:dateRange', list)

  return list;
})

/**
 * Whether the day is part of the selection or of the range the guest is hovering over. Both are filled
 * with a colour, so a swimwear day needs its blue fill in both cases.
 */
function dayInRange(day: ViewerDate): boolean
{
  return selected_days.value.includes(day.formatted) || calendar.hovered_days.includes(day.formatted);
}

/**
 * @param dates
 */
function formatDates(dates: Date[]): ViewerDate[]
{
  return dates.map((date): ViewerDate => {
    return {
      raw: date,
      formatted: format(date, "yyyy-MM-dd"),
      options: {
        inactive: props.disablePastDays && date < today
      }
    };
  });
}

/**
 * @param dates
 * @param dateFormat
 */
function formatDatesAsString(dates: Date[], dateFormat: string = "yyyy-MM-dd"): string[]
{
  return dates.map((date): string => {
      return format(date, dateFormat);
  });
}

function emitDayChange()
{
  emit('day:change', format(calendarStartDate.value, 'yyyy-MM-dd'), calendarEndDate.value ? format(calendarEndDate.value, 'yyyy-MM-dd') : null);
}

/**
 * @param event
 * @param day
 * @param index
 */
function handleDateSelection(event: Event, day: ViewerDate)
{
  // Be sure to negate inactive days.
  if (day.options.inactive && !day.options.deblockaged) {
    return;
  }

  // Whenever we are not in the multi date option, be sure to set both dates.
  if (!props.range) {
    calendarStartDate.value = day.raw; // Update the start date
    calendarEndDate.value = day.raw;   // Update the end date
    emitDayChange();
    return;
  }

  // Whenever we are in range:
  // - Check if we should reselect the start date (qwr/platform#2703)
  // - Check if we have an end date set: a click on or inside an existing range starts over.
  // - Check if the date is before the current date we have selected.
  if ((props.forceReselect && !calendar.is_reselected) || calendarEndDate.value || calendarStartDate.value > day.raw) {
    startFreshSelection(day);
    return;
  }

  // If we come here, we only have a startdate set, without a static range.
  // So disable the selection mode, and set the new end date.
  calendar.in_selection = false;
  calendar.hovered_days = [];

  calendarEndDate.value = day.raw;
  emitDayChange();
}

/**
 * Make the given day the arrival of a brand new selection. Every trace of a previous range
 * (end date and hover preview) is dropped first, so the range and extra night flags are
 * recalculated from the new arrival only.
 */
function startFreshSelection(day: ViewerDate)
{
  // Set the calendar as reselected, so we can proceed with the normal date picking.
  calendar.is_reselected = true;
  calendar.hovered_days = [];

  // Reset the end date before moving the start date: the clicked day can be part of the old
  // range and would otherwise keep its departure or extra night styling.
  calendarEndDate.value = null;
  calendarStartDate.value = day.raw;

  // It's possible we have a static range, if so, be sure to update it correctly already :)
  if(props.staticRange && props.staticRange > 0) {
    calendar.in_selection = false;
    calendarEndDate.value = addDays(day.raw, props.staticRange);
    emitDayChange();
    return;
  }

  // Otherwise: Be sure to activate the selection mode, the departure stays open.
  calendar.in_selection = true;
  emitDayChange();
}

/**
 * @param event
 */
function handleCalendarControl(event: "add" | "subtract")
{
  calendar.focus = (event === 'add' ? addMonths(calendar.focus, 1) : subMonths(calendar.focus, 1))

  emit('month:change', {event: event, focus: calendar.focus, dates: viewer_dates})
}

const canDecrement = computed(() => isAfter(calendar.focus, startOfMonth(today)));
const canIncrement = computed(() => isBefore(calendar.focus, addYears(today, 5)));

const nextCalendarMonth = computed(() => addMonths(calendar.focus, 1));
const previousCalendarMonth = computed(() => subMonths(calendar.focus, 1));

function localeFormatDate(date: Date, formatStr = 'MMMM yyyy') {
  return format(date, formatStr, {
    locale: languages[props.language as keyof typeof languages]
  });
}

/**
 * Handle mouse over events.
 * @param event
 * @param day
 */
function handleDateHover(event: Event, day?: ViewerDate)
{

  if(!day || (day.options.inactive && !day.options.deblockaged) || day.raw < calendarStartDate.value) {
    return (calendar.hovered_days = []);
  }

  if(calendar.in_selection) {
    calendar.hovered_days = formatDatesAsString(eachDayOfInterval({start: calendarStartDate.value, end: day.raw}))
  }
}
</script>
